import * as SentryCapacitor from '@sentry/capacitor';
import * as SentryVue from '@sentry/vue';

/* global __SENTRY_RELEASE__ */

/**
 * Initialize Sentry for the Capacitor app.
 * Must be called BEFORE app.mount() to capture component init errors.
 *
 * Uses @sentry/capacitor's init() with @sentry/vue's init as the sibling SDK,
 * which enables both Vue error handler integration and native crash capture
 * on iOS/Android through the Capacitor bridge.
 *
 * @param {import('vue').App} app - The Vue app instance (pre-mount)
 */
export function initSentry(app) {
  const dsn = import.meta.env.PG_SENTRY_DSN;

  // SDK is inert when DSN is empty (local dev without Sentry)
  if (!dsn) {
    return;
  }

  SentryCapacitor.init(
    {
      app,
      dsn,
      // Environment: detect from API URL (local vs production)
      release: (typeof __SENTRY_RELEASE__ !== 'undefined' && __SENTRY_RELEASE__)
        || import.meta.env.PG_APP_VERSION
        || 'unknown',
      environment: detectEnvironment(),
      // Disable performance monitoring for Sentry free tier
      tracesSampleRate: 0.0,
      // Capture all errors
      sampleRate: 1.0,
      // Enable PII -- single-owner app, user context aids debugging
      sendDefaultPii: true,
    },
    // Pass @sentry/vue init as the sibling SDK initializer
    // This integrates with Vue's app.config.errorHandler for component error context
    SentryVue.init,
  );
}

/**
 * Set the logged-in user on the Sentry scope.
 * Call after successful login or when user data is loaded from persisted store.
 *
 * @param {object|null} user - User object with id and email, or null to clear
 */
export function setSentryUser(user) {
  if (!import.meta.env.PG_SENTRY_DSN) {
    return;
  }

  if (user && user.id) {
    SentryCapacitor.setUser({
      id: String(user.id),
      email: user.email || undefined,
    });
  } else {
    SentryCapacitor.setUser(null);
  }
}

/**
 * Detect environment based on the API URL.
 * Production app talks to api.privategym.nl, local dev uses .localhost domains.
 */
function detectEnvironment() {
  const apiUrl = import.meta.env.PG_API_ROOT_URL || '';
  if (apiUrl.includes('localhost') || apiUrl.includes('127.0.0.1')) {
    return 'local';
  }
  return 'production';
}
